/**
 * PrintSettingsPanel — tenant-admin print customization (Phase 10 / Task 10.6).
 *
 * Rendered inside the (TENANT_ADMIN-gated) Settings page. Lets an admin set a
 * custom header line (<= 40 chars), the phone number printed under it (task #29), and
 * upload / replace / remove a logo that the server converts to a thermal-printer ESC/POS
 * raster. Inline-styled with the
 * dispatcher theme's CSS custom properties (matches InReviewSettingsPanel).
 */

import * as React from 'react';
import { formatPhoneAsTyped, formatPhoneForDisplay, parseUsPhone } from '@lodex-pro/shared/phone';
import {
  usePrintConfig,
  useUpdatePrintHeader,
  useUpdatePrintPhone,
  useUploadPrintLogo,
  useDeletePrintLogo,
} from './use-print-config';

const HEADER_MAX = 40;

const panelStyle: React.CSSProperties = {
  border: '1px solid var(--color-border-primary)',
  borderRadius: '10px',
  padding: '20px',
  backgroundColor: 'var(--color-bg-secondary, var(--color-bg-primary))',
  maxWidth: '620px',
};
const titleStyle: React.CSSProperties = {
  margin: '0 0 4px',
  fontSize: '18px',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
};
const descStyle: React.CSSProperties = {
  margin: '0 0 20px',
  fontSize: '13px',
  color: 'var(--color-text-secondary)',
};
const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  paddingTop: '16px',
  marginTop: '16px',
  borderTop: '1px solid var(--color-border-primary)',
};
const labelStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--color-text-primary)',
};
const inputStyle: React.CSSProperties = {
  height: '38px',
  padding: '0 10px',
  fontSize: '14px',
  borderRadius: '6px',
  border: '1px solid var(--color-border-primary)',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
};
const rowBetween: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
};
const helperStyle: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };
const counterStyle: React.CSSProperties = { fontSize: '12px', color: 'var(--color-text-tertiary)' };
const noteStyle: React.CSSProperties = {
  fontSize: '12px',
  fontStyle: 'italic',
  color: 'var(--color-text-tertiary)',
};
const actionRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginTop: '6px',
};
const primaryBtn: React.CSSProperties = {
  height: '38px',
  padding: '0 18px',
  fontWeight: 600,
  fontSize: '14px',
  borderRadius: 'var(--radius-pill)',
  border: 'none',
  cursor: 'pointer',
  backgroundColor: 'var(--color-accent-blue)',
  color: 'var(--color-on-accent)',
};
const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: 'transparent',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-border-primary)',
};
const dangerBtn: React.CSSProperties = {
  ...secondaryBtn,
  color: 'var(--color-danger)',
  borderColor: 'var(--color-danger)',
};
const okText: React.CSSProperties = { fontSize: '13px', color: 'var(--color-success)' };
const errText: React.CSSProperties = { fontSize: '13px', color: 'var(--color-danger)' };

export function PrintSettingsPanel(): React.JSX.Element {
  const { data, isLoading } = usePrintConfig();
  const updateHeader = useUpdatePrintHeader();
  const updatePhone = useUpdatePrintPhone();
  const uploadLogo = useUploadPrintLogo();
  const deleteLogo = useDeletePrintLogo();
  const fileRef = React.useRef<HTMLInputElement | null>(null);

  const [header, setHeader] = React.useState('');
  React.useEffect(() => {
    if (data !== undefined) setHeader(data.customHeader ?? '');
  }, [data]);

  // Task #29 — stored E.164, shown and edited as (405) 555-0123. Task #57 — formatted as
  // the user types, and re-shown formatted on save: re-saving the stored number returns
  // equal data, React Query keeps the same `data` object and the effect never re-runs.
  const [phone, setPhone] = React.useState('');
  const [phoneError, setPhoneError] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (data !== undefined) setPhone(data.phone !== null ? formatPhoneForDisplay(data.phone) : '');
  }, [data]);

  const hasLogo = data?.hasLogo ?? false;

  const onSaveHeader = (): void => {
    const trimmed = header.trim();
    updateHeader.mutate({ customHeader: trimmed === '' ? null : trimmed });
  };
  const onSavePhone = (): void => {
    const trimmed = phone.trim();
    if (trimmed === '') {
      setPhoneError(null);
      updatePhone.mutate({ phone: null });
      return;
    }
    const canonical = parseUsPhone(trimmed);
    if (canonical === '') {
      setPhoneError('Enter a 10-digit US phone number.');
      return;
    }
    setPhoneError(null);
    setPhone(formatPhoneForDisplay(canonical));
    updatePhone.mutate({ phone: canonical });
  };
  const onPickFile = (): void => fileRef.current?.click();
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file !== undefined) uploadLogo.mutate(file);
    e.target.value = ''; // allow re-picking the same file
  };

  return (
    <section style={panelStyle} aria-label="Print settings">
      <h2 style={titleStyle}>Print Settings</h2>
      <p style={descStyle}>
        Customize the header, phone number and logo printed on run and disposal tickets.
      </p>

      {/* ── Custom header ── */}
      <div style={fieldStyle}>
        <label htmlFor="print-header" style={labelStyle}>
          Custom header text
        </label>
        <input
          id="print-header"
          type="text"
          value={header}
          maxLength={HEADER_MAX}
          onChange={(e): void => setHeader(e.target.value)}
          disabled={isLoading}
          placeholder="e.g. ACME WATER HAULING"
          style={inputStyle}
        />
        <div style={rowBetween}>
          <span style={helperStyle}>Appears above &lsquo;lodeX Pro&rsquo; on printed tickets.</span>
          <span style={counterStyle}>
            {header.length}/{HEADER_MAX}
          </span>
        </div>
        <div style={actionRow}>
          <button
            type="button"
            onClick={onSaveHeader}
            disabled={updateHeader.isPending || isLoading}
            style={primaryBtn}
          >
            {updateHeader.isPending ? 'Saving…' : 'Save'}
          </button>
          {updateHeader.isSuccess && <span style={okText}>Saved.</span>}
          {updateHeader.isError && <span style={errText}>Save failed — try again.</span>}
        </div>
      </div>

      {/* ── Phone (task #29) ── */}
      <div style={fieldStyle}>
        <label htmlFor="print-phone" style={labelStyle}>
          Phone on tickets
        </label>
        <input
          id="print-phone"
          type="tel"
          inputMode="tel"
          value={phone}
          onChange={(e): void => {
            setPhone(formatPhoneAsTyped(e.target.value));
            setPhoneError(null);
          }}
          disabled={isLoading}
          placeholder="(405) 555-0123"
          style={inputStyle}
        />
        <span style={helperStyle}>
          Printed under your header on every ticket. Leave blank to print no phone number.
        </span>
        <div style={actionRow}>
          <button
            type="button"
            onClick={onSavePhone}
            disabled={updatePhone.isPending || isLoading}
            style={primaryBtn}
            aria-label="Save phone"
          >
            {updatePhone.isPending ? 'Saving…' : 'Save'}
          </button>
          {phoneError !== null && <span style={errText}>{phoneError}</span>}
          {phoneError === null && updatePhone.isSuccess && <span style={okText}>Saved.</span>}
          {phoneError === null && updatePhone.isError && (
            <span style={errText}>Save failed — try again.</span>
          )}
        </div>
        <span style={noteStyle}>Phone changes take effect on next app sync.</span>
      </div>

      {/* ── Logo ── */}
      <div style={fieldStyle}>
        <span style={labelStyle}>Ticket logo (black and white)</span>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg"
          onChange={onFileChange}
          style={{ display: 'none' }}
          data-testid="print-logo-input"
        />
        <span style={helperStyle}>{hasLogo ? 'Logo uploaded.' : 'No logo uploaded.'}</span>
        <div style={actionRow}>
          <button
            type="button"
            onClick={onPickFile}
            disabled={uploadLogo.isPending}
            style={hasLogo ? secondaryBtn : primaryBtn}
          >
            {uploadLogo.isPending ? 'Uploading…' : hasLogo ? 'Replace Logo' : 'Upload Logo'}
          </button>
          {hasLogo && (
            <button
              type="button"
              onClick={(): void => deleteLogo.mutate()}
              disabled={deleteLogo.isPending}
              style={dangerBtn}
            >
              {deleteLogo.isPending ? 'Removing…' : 'Remove Logo'}
            </button>
          )}
        </div>
        <span style={helperStyle}>
          Logo is converted to black and white for thermal printing (max 384px wide). It is used
          only on printed tickets: your colour logo for invoices, emails and the menu is under
          Company logo.
        </span>
        {uploadLogo.isError && (
          <span style={errText}>{uploadLogo.error?.message ?? 'Upload failed.'}</span>
        )}
        <span style={noteStyle}>Logo changes take effect on next app sync.</span>
      </div>
    </section>
  );
}
